/**
 * Card Component — Yoluno design system
 *
 * Default: white surface, var(--radius-lg) corners, 1px var(--border-soft),
 * var(--shadow-md). Glass variant for translucent overlay sections.
 */

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const cardVariants = cva(
  'rounded-[var(--radius-lg)] text-[var(--text-body)]',
  {
    variants: {
      variant: {
        default:
          'bg-[var(--surface-card)] border border-[var(--border-soft)] shadow-[var(--shadow-md)]',
        glass: [
          'bg-[rgba(255,255,255,0.45)] backdrop-blur-[16px]',
          'border border-[rgba(180,222,215,0.4)]',
          'shadow-[0_4px_24px_rgba(61,214,200,0.08),inset_0_1px_0_rgba(255,255,255,0.6)]',
          'transition-[box-shadow,border-color] duration-300',
          'hover:border-[rgba(61,214,200,0.5)]',
          'hover:shadow-[0_8px_32px_rgba(61,214,200,0.15),inset_0_1px_0_rgba(255,255,255,0.6)]',
        ].join(' '),
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ variant }), className)}
      {...props}
    />
  )
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex flex-col space-y-1.5 p-6', className)}
      {...props}
    />
  )
);
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn(
        'text-h4 font-display font-semibold leading-[var(--lh-tight)] tracking-tight text-[var(--text-strong)]',
        className
      )}
      {...props}
    />
  )
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-body-sm text-[var(--text-muted)]', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
  )
);
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props} />
  )
);
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
